import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

interface SettingsResourceDialogListItemProps {
  onClick: Handler;
  isSelected: boolean;
  iconPart: ReactNode;
  rightIconPart?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
}

/*
 * Style.
 */

const StyledTagDiv = styled(ChromelessButton)`
  padding: 4px 0;
  margin: 0 8px;
  display: grid;
  height: 48px;
  align-items: center;
  background-color: ${(p) => p.theme.greys.white};

  grid-template-areas: '. highlight . label . chevron .';
  grid-template-columns: 12px auto 12px 1fr 8px auto 12px;
  border-radius: 8px;

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledHighlightDiv = styled.div`
  grid-area: highlight;
`;

interface TagLabelStyleProps {
  $hasSubtitle?: boolean;
}
const StyledLabelDiv = styled.div<TagLabelStyleProps>`
  grid-area: label;

  ${(p) => addLabelStyles(Boolean(p.$hasSubtitle))};
`;

function addLabelStyles(hasSubtitle: boolean) {
  if (hasSubtitle) {
    return css`
      display: grid;
      grid-row-gap: 4px;
      grid-auto-flow: row;
      grid-auto-rows: auto;
      align-items: center;
    `;
  }

  return css`
    ${flexCenter};
  `;
}

const StyledTagNameDiv = styled.div<{$isSelected?: boolean}>`
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${(p) => (p.$isSelected ? fontWeights.bold : fontWeights.normal)};
  font-size: ${fontSizes.medium};
`;

const StyledTagSubtitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.verySmall};
`;

const StyledChevronDiv = styled.div`
  grid-area: chevron;
`;

/*
 * Component.
 */

export const SettingsResourceDialogListItem: FC<SettingsResourceDialogListItemProps> = (props) => (
  <StyledTagDiv onClick={props.onClick}>
    <StyledHighlightDiv>{props.iconPart}</StyledHighlightDiv>
    <StyledLabelDiv $hasSubtitle={Boolean(props.subtitle)}>
      <StyledTagNameDiv $isSelected={props.isSelected}>{props.title}</StyledTagNameDiv>
      {props.subtitle && <StyledTagSubtitleDiv>{props.subtitle}</StyledTagSubtitleDiv>}
    </StyledLabelDiv>
    <StyledChevronDiv>{props.rightIconPart}</StyledChevronDiv>
  </StyledTagDiv>
);
